import { CloseRounded, ExpandLess, ExpandMore, SearchRounded } from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import SidebarIcon from "@/components/SidebarIcon";
import { resolveSidebarLabel } from "@/constants";
import { useTranslation } from "@/context/I18nProvider";
import { useAdminSidebars } from "@/hooks";
import { useSelector } from "@/store";
import { AdminSidebar, AdminSidebarStatus } from "@/types";

interface SidebarNode extends AdminSidebar {
  // Localized via the row's stable name; also what the menu search matches.
  text: string;
  children: SidebarNode[];
}

// Build a parent → children tree from the flat list returned by the API.
// Rows whose parent isn't present surface as top-level so nothing silently
// disappears if the data is mid-migration.
const buildTree = (rows: AdminSidebar[], labelOf: (row: AdminSidebar) => string): SidebarNode[] => {
  const active = rows.filter((row) => row.status === AdminSidebarStatus.ACTIVE);
  const byId = new Map<string, SidebarNode>();
  active.forEach((row) => byId.set(String(row._id), { ...row, text: labelOf(row), children: [] }));

  const roots: SidebarNode[] = [];
  byId.forEach((node) => {
    const parentId = node.parent ? String(node.parent) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortBranch = (branch: SidebarNode[]) => {
    branch.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
    branch.forEach((node) => sortBranch(node.children));
  };
  sortBranch(roots);

  return roots;
};

// Keep a node when it or anything below it matches; a matching group keeps all its children.
const filterTree = (branch: SidebarNode[], query: string): SidebarNode[] =>
  branch.flatMap((node) => {
    if (node.text.toLowerCase().includes(query)) return [node];
    const children = filterTree(node.children, query);
    return children.length ? [{ ...node, children }] : [];
  });

const firstLeaf = (branch: SidebarNode[]): SidebarNode | undefined => {
  for (const node of branch) {
    if (!node.children.length) return node;
    const leaf = firstLeaf(node.children);
    if (leaf) return leaf;
  }
  return undefined;
};

// Does this branch contain the current path? Used to auto-expand groups whose
// (possibly nested) child is the active route.
const containsPath = (node: SidebarNode, path: string): boolean =>
  node.url === path || node.children.some((child) => containsPath(child, path));

// Renders one sidebar node. A node with children is a collapsible group that
// recurses (so sub-parents like "Template Footer" nest properly); a leaf is a
// NavLink. Indentation grows with depth.
function NavNode({
  node,
  depth,
  forceOpen,
  onNavigate,
}: {
  node: SidebarNode;
  depth: number;
  forceOpen: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const isGroup = node.children.length > 0;
  const holdsActive = containsPath(node, location.pathname);
  const [open, setOpen] = useState(isGroup && holdsActive);
  const itemRef = useRef<HTMLLIElement>(null);
  const pl = 2 + depth * 2;

  // Open the group of a page reached from elsewhere (breadcrumb, dashboard link).
  useEffect(() => {
    if (isGroup && holdsActive) setOpen(true);
  }, [isGroup, holdsActive]);

  // A deep page should not leave its entry hidden below the fold of a long menu.
  useEffect(() => {
    if (isGroup || node.url !== location.pathname) return;
    const timer = window.setTimeout(
      () => itemRef.current?.scrollIntoView({ block: "nearest" }),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [isGroup, node.url, location.pathname]);

  if (!isGroup) {
    return (
      <ListItem ref={itemRef} disablePadding sx={{ display: "block" }}>
        <ListItemButton component={NavLink} to={node.url} end onClick={onNavigate} sx={{ pl }}>
          <ListItemIcon>
            <SidebarIcon name={node.icon} />
          </ListItemIcon>
          <ListItemText primary={node.text} />
        </ListItemButton>
      </ListItem>
    );
  }

  const expanded = forceOpen || open;

  return (
    <>
      <ListItem disablePadding sx={{ display: "block" }}>
        <ListItemButton
          onClick={() => setOpen(!expanded)}
          aria-expanded={expanded}
          // The group of the current page stays readable while collapsed.
          sx={{ pl, ...(holdsActive && { opacity: "1 !important" }) }}
        >
          <ListItemIcon>
            <SidebarIcon name={node.icon} />
          </ListItemIcon>
          <ListItemText primary={node.text} />
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {node.children.map((child) => (
            <NavNode
              key={child._id}
              node={child}
              depth={depth + 1}
              forceOpen={forceOpen}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

interface MenuContentProps {
  onNavigate?: () => void;
}

export default function MenuContent({ onNavigate }: MenuContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rows = useAdminSidebars();
  const { user } = useSelector((state) => state.auth);
  const roleId = user?.role ? String(user.role) : null;
  const [query, setQuery] = useState<string>("");
  const needle = query.trim().toLowerCase();

  // Filter rows to those that explicitly grant the current visitor's role.
  // An empty `roleIds` means the row has not been granted to anyone yet, so
  // it's hidden by design. Parent visibility isn't enforced - a leaf whose
  // parent isn't granted is promoted to the root by buildTree, which is
  // fine because the operator may want to expose a single page from a
  // group without exposing the rest.
  const tree = useMemo(() => {
    if (!roleId) return [];
    const allowed = rows.filter((row) => (row.roleIds ?? []).map(String).includes(roleId));

    return buildTree(allowed, (row) => resolveSidebarLabel(row.name, row.label, t));
  }, [rows, roleId, t]);

  const visible = useMemo(() => (needle ? filterTree(tree, needle) : tree), [tree, needle]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") setQuery("");
    if (event.key !== "Enter") return;
    const leaf = needle ? firstLeaf(visible) : undefined;
    if (!leaf) return;
    navigate(leaf.url);
    setQuery("");
    onNavigate?.();
  };

  return (
    <Stack sx={{ flexGrow: 1, minHeight: 0 }}>
      {/* Stays above the scrolling list so the search is reachable from anywhere in it. */}
      <Box
        sx={{
          px: 1.5,
          pt: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
        }}
      >
        <TextField
          size="small"
          fullWidth
          value={query}
          placeholder={t("common.searchMenu")}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{
            htmlInput: { "aria-label": t("common.searchMenu") },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t("common.clear")}
                    onClick={() => setQuery("")}
                    edge="end"
                  >
                    <CloseRounded fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
      <List dense sx={{ px: 1 }}>
        {visible.map((node) => (
          <NavNode
            key={node._id}
            node={node}
            depth={0}
            forceOpen={!!needle}
            onNavigate={onNavigate}
          />
        ))}
        {needle && !visible.length && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 1, py: 2, textAlign: "center" }}
          >
            {t("common.noMatches")}
          </Typography>
        )}
      </List>
    </Stack>
  );
}
